// Classic Card Games — dispatcher module.
// Implements the 6 required game module exports by delegating to subgames.
// State is a discriminated union so each subgame has its own typed state.

import * as War from './war';
import * as Holdem from './texas-holdem';
import * as Gin from './gin-rummy';
import * as Hearts from './hearts';
import * as Spades from './spades';

export { War, Holdem, Gin, Hearts, Spades };

// --- Config ---

export interface ClassicCardsConfig {
  game: 'war' | 'texas-holdem' | 'gin-rummy' | 'hearts' | 'spades';
  rulesetId?: string;
  // War
  warVariant?: War.WarVariant;
  warWinningScore?: number;
  // Texas Hold'em
  holdemStartingChips?: number;
  holdemSmallBlind?: number;
  holdemBigBlind?: number;
  // Gin Rummy
  ginTargetScore?: number;
  // Hearts
  heartsTargetScore?: number;
  // Spades
  spadesTargetScore?: number;
  spadesNilBonus?: number;
  spadesBagPenalty?: number;
}

export const DEFAULT_CONFIG: ClassicCardsConfig = {
  game: 'war',
  warVariant: 'regular',
  warWinningScore: 5,
};

// --- State ---

export type ClassicCardsState =
  | { game: 'war';          data: War.WarState }
  | { game: 'texas-holdem'; data: Holdem.HoldemState }
  | { game: 'gin-rummy';    data: Gin.GinState }
  | { game: 'hearts';       data: Hearts.HeartsState }
  | { game: 'spades';       data: Spades.SpadesState };

// --- Actions ---

export type ClassicCardsAction =
  | { game: 'war';          action: War.WarAction }
  | { game: 'texas-holdem'; action: Holdem.HoldemAction }
  | { game: 'gin-rummy';    action: Gin.GinAction }
  | { game: 'hearts';       action: Hearts.HeartsAction }
  | { game: 'spades';       action: Spades.SpadesAction };

// --- View ---

export type ClassicCardsView =
  | { game: 'war';          data: War.WarView }
  | { game: 'texas-holdem'; data: Holdem.HoldemView }
  | { game: 'gin-rummy';    data: Gin.GinView }
  | { game: 'hearts';       data: Hearts.HeartsView }
  | { game: 'spades';       data: Spades.SpadesView };

export type ClassicCardsActionType = string;

// --- PlayerInit ---

export interface PlayerInit {
  id: string;
  name: string;
  isBot?: boolean;
}

// --- Config extractors ---

function warConfig(c: ClassicCardsConfig): War.WarConfig {
  return { variant: c.warVariant ?? 'regular', winningScore: c.warWinningScore ?? 5 };
}

function holdemConfig(c: ClassicCardsConfig): Holdem.HoldemConfig {
  return {
    startingChips: c.holdemStartingChips ?? 1000,
    smallBlind: c.holdemSmallBlind ?? 10,
    bigBlind: c.holdemBigBlind ?? 20,
  };
}

function ginConfig(c: ClassicCardsConfig): Gin.GinConfig {
  return { targetScore: c.ginTargetScore ?? 100 };
}

function heartsConfig(c: ClassicCardsConfig): Hearts.HeartsConfig {
  return { targetScore: c.heartsTargetScore ?? 100 };
}

function spadesConfig(c: ClassicCardsConfig): Spades.SpadesConfig {
  return {
    targetScore: c.spadesTargetScore ?? 500,
    nilBonus: c.spadesNilBonus ?? 100,
    bagPenalty: c.spadesBagPenalty ?? 100,
  };
}

// --- Required exports ---

export function createGame(config: ClassicCardsConfig, players: PlayerInit[], seed?: number): ClassicCardsState {
  switch (config.game) {
    case 'war':
      return { game: 'war', data: War.createGame(warConfig(config), players, seed) };
    case 'texas-holdem':
      return { game: 'texas-holdem', data: Holdem.createGame(holdemConfig(config), players, seed) };
    case 'gin-rummy':
      return { game: 'gin-rummy', data: Gin.createGame(ginConfig(config), players, seed) };
    case 'hearts':
      return { game: 'hearts', data: Hearts.createGame(heartsConfig(config), players, seed) };
    case 'spades':
      return { game: 'spades', data: Spades.createGame(spadesConfig(config), players, seed) };
  }
}

export function applyAction(state: ClassicCardsState, action: ClassicCardsAction): ClassicCardsState {
  if (state.game !== action.game) return state;
  switch (state.game) {
    case 'war':
      return { game: 'war', data: War.applyAction(state.data, (action as { game: 'war'; action: War.WarAction }).action) };
    case 'texas-holdem':
      return { game: 'texas-holdem', data: Holdem.applyAction(state.data, (action as { game: 'texas-holdem'; action: Holdem.HoldemAction }).action) };
    case 'gin-rummy':
      return { game: 'gin-rummy', data: Gin.applyAction(state.data, (action as { game: 'gin-rummy'; action: Gin.GinAction }).action) };
    case 'hearts':
      return { game: 'hearts', data: Hearts.applyAction(state.data, (action as { game: 'hearts'; action: Hearts.HeartsAction }).action) };
    case 'spades':
      return { game: 'spades', data: Spades.applyAction(state.data, (action as { game: 'spades'; action: Spades.SpadesAction }).action) };
  }
}

export function getPlayerView(state: ClassicCardsState, playerId: string | null): ClassicCardsView {
  switch (state.game) {
    case 'war':
      return { game: 'war', data: War.getPlayerView(state.data, playerId) };
    case 'texas-holdem':
      return { game: 'texas-holdem', data: Holdem.getPlayerView(state.data, playerId) };
    case 'gin-rummy':
      return { game: 'gin-rummy', data: Gin.getPlayerView(state.data, playerId) };
    case 'hearts':
      return { game: 'hearts', data: Hearts.getPlayerView(state.data, playerId) };
    case 'spades':
      return { game: 'spades', data: Spades.getPlayerView(state.data, playerId) };
  }
}

export function describe(config: ClassicCardsConfig): string {
  switch (config.game) {
    case 'war':          return War.describe(warConfig(config));
    case 'texas-holdem': return Holdem.describe(holdemConfig(config));
    case 'gin-rummy':    return Gin.describe(ginConfig(config));
    case 'hearts':       return Hearts.describe(heartsConfig(config));
    case 'spades':       return Spades.describe(spadesConfig(config));
  }
}

export function getValidActions(state: ClassicCardsState, playerId: string): string[] {
  switch (state.game) {
    case 'war':          return War.getValidActions(state.data, playerId);
    case 'texas-holdem': return Holdem.getValidActions(state.data, playerId);
    case 'gin-rummy':    return Gin.getValidActions(state.data, playerId);
    case 'hearts':       return Hearts.getValidActions(state.data, playerId);
    case 'spades':       return Spades.getValidActions(state.data, playerId);
  }
}

export function getBotAction(playerId: string, state: ClassicCardsState): ClassicCardsAction {
  switch (state.game) {
    case 'war': {
      const player = state.data.players.find(p => p.id === playerId)!;
      return { game: 'war', action: { type: War.getBotAction(player, state.data), playerId } };
    }
    case 'texas-holdem':
      return { game: 'texas-holdem', action: Holdem.getBotAction(playerId, state.data) };
    case 'gin-rummy':
      return { game: 'gin-rummy', action: Gin.getBotAction(playerId, state.data) };
    case 'hearts':
      return { game: 'hearts', action: Hearts.getBotAction(playerId, state.data) };
    case 'spades':
      return { game: 'spades', action: Spades.getBotAction(playerId, state.data) };
  }
}

// Convenience: get the overall winner ID regardless of subgame
export function getWinnerId(state: ClassicCardsState): string | null {
  switch (state.game) {
    case 'war':          return state.data.winnerId;
    case 'texas-holdem': return state.data.winnerId;
    case 'gin-rummy':    return state.data.winnerId;
    case 'hearts':       return state.data.winnerId;
    case 'spades':       return state.data.winnerId;
  }
}

export function isComplete(state: ClassicCardsState): boolean {
  switch (state.game) {
    case 'war':          return state.data.phase === 'complete';
    case 'texas-holdem': return state.data.phase === 'complete';
    case 'gin-rummy':    return state.data.phase === 'complete';
    case 'hearts':       return state.data.phase === 'complete';
    case 'spades':       return state.data.phase === 'complete';
  }
}
